/**
 * Shares reusable logic for editing comments or replies.
 */
import { useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { TItemType } from '@modules/items';
import { useUpdateContent } from '../api';
import { schemaComment, TFormDataComment } from '../components/form/CommentForm';

type TUseEditCommentOrReplyParams = {
  itemId: string;
  itemType: TItemType;
  commentId: string;
  replyId?: string; // If editing a reply, pass it here
  initialContent?: string;
};

type TReturn = {
  isEditing: boolean;
  setIsEditing: (val: boolean) => void;
  editForm: UseFormReturn<TFormDataComment>;
  handleEditSubmit: (data: TFormDataComment) => void;
  isEditable: boolean;
};

export const useEditCommentOrReply = (params: TUseEditCommentOrReplyParams): TReturn => {
  const { itemId, itemType, commentId, replyId, initialContent } = params;

  // Disable editing if `initialContent` is empty
  const isEditable = !!initialContent;

  // Track local editing state
  const [isEditing, setIsEditing] = useState(false);

  // “Edit” form
  const editForm = useForm<TFormDataComment>({
    resolver: yupResolver(schemaComment),
    defaultValues: { text: initialContent },
  });

  // The actual mutation
  const updateContent = useUpdateContent();

  const handleEditSubmit = (data: TFormDataComment) => {
    updateContent.mutate(
      {
        itemId,
        itemType,
        commentId,
        replyId: replyId || '', // empty if no reply
        content: data.text,
      },
      {
        onSuccess: () => {
          setIsEditing(false);
        },
      }
    );
  };

  return {
    isEditing,
    setIsEditing,
    editForm,
    handleEditSubmit,
    isEditable,
  };
};
